import { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel } from '@mui/material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { resetDialogMessage } from '../../../store/states/DialogSlice';
import { setAlertMessage } from '../../../store/states/AlertMessageSlice';
import { retriggerProductRelease } from '../../../store/states/ProductReleaseSlice';
import type { IAPIResponse } from '../../../../types';
import { t } from 'i18next';
import { ProductReleaseActivities } from '../../../data/Constants';

interface IRetriggerProductReleaseFormProps {
  id: string;
}

export const RetriggerProductReleaseForm = (props: IRetriggerProductReleaseFormProps) => {
  const { id } = props;
  const dispatch = useDispatch<AppDispatch>();

  const activityList = [...ProductReleaseActivities];

  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);

  const allSelected = activityList.length > 0 && selectedActivities.length === activityList.length;
  const someSelected = selectedActivities.length > 0 && !allSelected;

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedActivities([...activityList]);
    } else {
      setSelectedActivities([]);
    }
  };

  const handleCheckboxChange = (activity: string) => {
    setSelectedActivities((prev) =>
      prev.includes(activity) ? prev.filter((a) => a !== activity) : [...prev, activity]
    );
  };

  const handleSubmit = () => {
    const payload = { id, activities: selectedActivities };
    dispatch(retriggerProductRelease(payload)).then((res) => {
      const response = res.payload as IAPIResponse;
      if (response?.data) {
        dispatch(
          setAlertMessage({
            show: true,
            message: t('messages.success.retriggeredSuccessfully'),
            type: 'success',
          })
        );
      }
      dispatch(resetDialogMessage());
    });
  };

  const handleCancel = () => {
    dispatch(resetDialogMessage());
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <fieldset className="inputFieldset w-100 mt-1em">
        <legend className="inputLegend">{t('labels.activities')}</legend>
        <FormControlLabel
          label={t('labels.selectAll')}
          labelPlacement="end"
          control={
            <Checkbox
              color="primary"
              indeterminate={someSelected}
              checked={allSelected}
              onChange={handleSelectAll}
            />
          }
        />
        <div className="checkbox-grid">
          {activityList.map((activity) => {
            const isSelected = selectedActivities.includes(activity);
            const translatedActivity = t('labels.' + activity);
            const displayLabel =
              translatedActivity === 'labels.' + activity ? activity : translatedActivity;
            return (
              <FormControlLabel
                key={activity}
                label={displayLabel}
                labelPlacement="end"
                control={
                  <Checkbox checked={isSelected} onChange={() => handleCheckboxChange(activity)} />
                }
              />
            );
          })}
        </div>
      </fieldset>
      <Box className="formActionButtons">
        <Button
          variant="contained"
          size="medium"
          className="text-capitalize"
          disabled={selectedActivities.length === 0}
          onClick={handleSubmit}
        >
          {t('button.ok')}
        </Button>
        <Button size="medium" className="text-capitalize" onClick={handleCancel}>
          {t('button.cancel')}
        </Button>
      </Box>
    </Box>
  );
};
